import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { ApiResponse } from "../interfaces/all";

// Em desenvolvimento, usamos o caminho relativo para passar pelo proxy do Vite e
// evitar problemas de CORS e envio de cookies (SameSite).
const API_URL = import.meta.env.DEV
  ? "/api"
  : `${import.meta.env.VITE_BACKEND_URL}/api`;

const API_TIMEOUT = 10000;
const TEMPLATES_API_TIMEOUT = 60000; // 60 segundos para carregar templates
const BULK_API_TIMEOUT = 300000; // 5 minutos para envios em massa
const LARGE_BULK_API_TIMEOUT = 1800000; // 30 minutos para volumes muito grandes (1000+)
const HISTORY_API_TIMEOUT = 60000; // 60 segundos para histórico de cópias
const CONTACTS_API_TIMEOUT = 60000; // 60 segundos para contatos de leads

let errorHandler: ((error: unknown) => void) | null = null;

export const setErrorHandler = (handler: (error: unknown) => void) => {
  errorHandler = handler;
};

class ApiService {
  private api: AxiosInstance;

  constructor() {
    if (!import.meta.env.VITE_BACKEND_URL) {
      console.warn(
        "⚠️ [ApiService] VITE_BACKEND_URL is not defined in environment variables",
      );
    }

    this.api = axios.create({
      baseURL: API_URL,
      withCredentials: true,
      timeout: API_TIMEOUT,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    this.api.interceptors.request.use(
      (config) => {
        // Envia o token via header se existir no localStorage, atuando como fallback
        // aos cookies httpOnly em casos de proxy/CORS/SameSite.
        const token = localStorage.getItem("wpp_access_token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Envia o companyId para sobrepor eventuais cookies de CRM no localhost
        const companyId = localStorage.getItem("wpp_company_id");
        if (companyId) {
          config.headers["x-client-company-id"] = companyId;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error: AxiosError) => {
        if (errorHandler) {
          errorHandler(error);
        }
        return Promise.reject(error);
      },
    );
  }

  public async get<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    if (url === "undefined" || url === undefined) {
      console.error("❌ [ApiService] GET request with undefined URL prevented");
      throw new Error("API call with undefined URL");
    }
    console.log(`📡 [ApiService] GET: ${url}`);
    const response = await this.api.get<ApiResponse<T>>(url, config);
    return response.data;
  }

  public async getWithTimeout<T>(
    url: string,
    timeout: number,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    if (url === "undefined" || url === undefined) {
      console.error("❌ [ApiService] GET request with undefined URL prevented");
      throw new Error("API call with undefined URL");
    }
    const response = await this.api.get<ApiResponse<T>>(url, {
      ...config,
      timeout,
    });
    return response.data;
  }

  public async getRaw<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    if (url === "undefined" || url === undefined) {
      console.error(
        "❌ [ApiService] GET (raw) request with undefined URL prevented",
      );
      throw new Error("API call with undefined URL");
    }
    return this.api.get<T>(url, config);
  }

  public async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    if (url === "undefined" || url === undefined) {
      console.error(
        "❌ [ApiService] POST request with undefined URL prevented",
      );
      throw new Error("API call with undefined URL");
    }
    console.log(`📡 [ApiService] POST: ${url}`);
    const response = await this.api.post<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  public async postBulk<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    if (url === "undefined" || url === undefined) {
      console.error(
        "❌ [ApiService] POST (bulk) request with undefined URL prevented",
      );
      throw new Error("API call with undefined URL");
    }
    const response = await this.api.post<ApiResponse<T>>(url, data, {
      ...config,
      timeout: BULK_API_TIMEOUT,
    });
    return response.data;
  }

  public async postLargeBulk<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    if (url === "undefined" || url === undefined) {
      console.error(
        "❌ [ApiService] POST (large bulk) request with undefined URL prevented",
      );
      throw new Error("API call with undefined URL");
    }
    const response = await this.api.post<ApiResponse<T>>(url, data, {
      ...config,
      timeout: LARGE_BULK_API_TIMEOUT,
    });
    return response.data;
  }

  public async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    if (url === "undefined" || url === undefined) {
      console.error("❌ [ApiService] PUT request with undefined URL prevented");
      throw new Error("API call with undefined URL");
    }
    console.log(`📡 [ApiService] PUT: ${url}`);
    const response = await this.api.put<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  public async delete<T>(
    url: string,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    if (url === "undefined" || url === undefined) {
      console.error(
        "❌ [ApiService] DELETE request with undefined URL prevented",
      );
      throw new Error("API call with undefined URL");
    }
    console.log(`📡 [ApiService] DELETE: ${url}`);
    const response = await this.api.delete<ApiResponse<T>>(url, config);
    return response.data;
  }

  public async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<ApiResponse<T>> {
    if (url === "undefined" || url === undefined) {
      console.error(
        "❌ [ApiService] PATCH request with undefined URL prevented",
      );
      throw new Error("API call with undefined URL");
    }
    console.log(`📡 [ApiService] PATCH: ${url}`);
    const response = await this.api.patch<ApiResponse<T>>(url, data, config);
    return response.data;
  }
}

export const apiService = new ApiService();

// Exportar timeout para uso em configurações específicas
export const TEMPLATES_TIMEOUT = TEMPLATES_API_TIMEOUT;
export const HISTORY_TIMEOUT = HISTORY_API_TIMEOUT;
export const CONTACTS_TIMEOUT = CONTACTS_API_TIMEOUT;
export const BULK_TIMEOUT = BULK_API_TIMEOUT;
